import { useState } from 'react';
import { supabase } from '../../lib/supabase';

interface Props {
  invoiceId: string;
  invoiceName: string;
  recipientEmail: string;
  compact?: boolean;
}

type SendState =
  | { kind: 'idle' }
  | { kind: 'confirming' }
  | { kind: 'busy' }
  | { kind: 'success'; at: number }
  | { kind: 'error'; message: string };

export function SendInvoiceButton({ invoiceId, invoiceName, recipientEmail, compact }: Props) {
  const [state, setState] = useState<SendState>({ kind: 'idle' });

  async function handleSend() {
    setState({ kind: 'busy' });
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Niet ingelogd');

      const response = await fetch('/api/invoices/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ invoiceId }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Versturen mislukt' }));
        throw new Error(err.error || 'Versturen mislukt');
      }
      setState({ kind: 'success', at: Date.now() });
      setTimeout(() => setState({ kind: 'idle' }), 4000);
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Versturen mislukt',
      });
    }
  }

  if (state.kind === 'success') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        Verzonden
      </span>
    );
  }

  if (state.kind === 'confirming') {
    return (
      <div className="inline-flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-md px-2.5 py-1.5">
        <span className="text-xs text-indigo-900">
          Versturen naar <strong>{recipientEmail || '—'}</strong>?
        </span>
        <button
          type="button"
          onClick={handleSend}
          className="text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded px-2 py-0.5"
        >
          Ja, verstuur
        </button>
        <button
          type="button"
          onClick={() => setState({ kind: 'idle' })}
          className="text-xs text-slate-600 hover:text-slate-900"
        >
          Annuleren
        </button>
      </div>
    );
  }

  if (state.kind === 'busy') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-slate-500">
        <span className="w-3 h-3 border-2 border-slate-300 border-t-indigo-600 rounded-full animate-spin" />
        Versturen...
      </span>
    );
  }

  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={() => setState({ kind: 'confirming' })}
        className={
          compact
            ? 'inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700'
            : 'inline-flex items-center gap-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md px-2.5 py-1.5'
        }
        title={`Verstuur factuur ${invoiceName}`}
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
        </svg>
        Verstuur factuur
      </button>
      {state.kind === 'error' && (
        <span className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-0.5">
          {state.message}
        </span>
      )}
    </div>
  );
}
